import {
  startTransition,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  Box,
  chakra,
  DatePicker as ChakraDatePicker,
  HStack,
  IconButton,
  Separator,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CalendarMonth, Close, Edit, Schedule } from '@m/icons';
import { UsDollarWithCents } from '@m/utils/USdollarAmount';
import { addHours, format, isSameDay, startOfDay } from 'date-fns';
import { DateTime } from 'luxon';

import { Button } from '@m/components-ui/button';
import {
  DatePicker,
  dateToDateValue,
  dateValueToDate,
  dateValuesForDatetimeRange,
} from '@m/components-ui/date-and-time/date-picker';
import {
  DatePickerMenuBody,
  DateTimeSection,
} from '@m/components-ui/date-and-time/date-picker-menu';
import {
  datetimesFromPickerDays,
  previousEndTimeIfStartUnchanged,
} from '@m/components-ui/date-and-time/rangeCalendarClick';
import { addDefaultReservationDuration } from '@m/components-ui/date-and-time/timeUtils';
import {
  combineDateWithSelectableTime,
  getEarliestSelectableDateTime,
  getSelectableTimeOptions,
} from '@m/components-ui/date-and-time/useCalendar';
import { InformationCard } from '@m/components-ui/information-card';
import { SectionHeader } from '@m/components-ui/mobile-rows';
import { SubpageHeader } from '@m/components-ui/subpage-header';
import { Tag } from '@m/components-ui/tag';

// ── Helpers ───────────────────────────────────────────────────────────────────

function dateInputBorderColor(disabled: boolean | undefined, isOpen: boolean | undefined): string {
  if (disabled) return 'stroke.border.disabled';
  if (isOpen) return 'stroke.border.focused';
  return 'stroke.border.default';
}

function formatDateRange(start: Date, end: Date): string {
  const todayStart = startOfDay(new Date());
  const sameDay = isSameDay(start, end);
  if (isSameDay(start, todayStart) && sameDay) {
    return `Today, ${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`;
  }
  if (sameDay) {
    return `${format(start, 'MMM d, h:mm a')} – ${format(end, 'h:mm a')}`;
  }
  return `${format(start, 'MMM d, h:mm a')} – ${format(end, 'MMM d, h:mm a')}`;
}

// Encodes a picked wall-clock as a lot-local instant so callers land on the
// exact time (and price) the user saw. Falls back to browser-local if the
// timezone is unavailable.
export function toLotInstant(d: Date, timezone?: string): number {
  if (!timezone) return d.getTime();
  const dt = DateTime.fromISO(format(d, "yyyy-MM-dd'T'HH:mm:ss"), { zone: timezone });
  return dt.isValid ? dt.toMillis() : d.getTime();
}

function formatDuration(start: Date, end: Date): string {
  const totalMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    const parts = [`${days}d`, `${hours}h`];
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  }
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

/** Nearest overflow-y ancestor — the location details panel, not `window`. */
function getOverflowYScrollParent(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
}

/** Scrolls `el` just enough to fit in its overflow parent (`block: 'nearest'`). */
function scrollChildIntoNearestOverflow(el: HTMLElement) {
  const parent = getOverflowYScrollParent(el);
  if (!parent) {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return;
  }
  const elRect = el.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();
  const paddingBottom = Number.parseFloat(getComputedStyle(parent).scrollPaddingBottom) || 0;
  const paddingTop = Number.parseFloat(getComputedStyle(parent).scrollPaddingTop) || 0;
  let delta = 0;
  if (elRect.bottom > parentRect.bottom - paddingBottom) {
    delta = elRect.bottom - (parentRect.bottom - paddingBottom);
  }
  if (elRect.top - delta < parentRect.top + paddingTop) {
    delta = elRect.top - (parentRect.top + paddingTop);
  }
  if (delta !== 0) parent.scrollTop += delta;
}

function DateInputBox({
  disabled,
  isOpen,
  label,
  placeholder = 'Select date and time',
  value,
  onOpen,
}: {
  disabled?: boolean;
  isOpen?: boolean;
  label: string;
  onOpen: () => void;
  placeholder?: string;
  value?: Date;
}) {
  return (
    <Stack gap="1">
      <Text color={disabled ? 'text.disabled' : 'text.secondary'} variant="SmallBodyMedium">
        {label}
      </Text>
      <chakra.button
        alignItems="center"
        aria-disabled={disabled}
        disabled={disabled}
        bg={disabled ? 'fill.background.default' : 'fill.input.enabled'}
        border={isOpen ? '2px solid' : '1px solid'}
        borderColor={dateInputBorderColor(disabled, isOpen)}
        borderRadius="md"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        display="flex"
        h="48px"
        justifyContent="space-between"
        px="4"
        w="full"
        onClick={() => !disabled && onOpen()}
      >
        <Text color={value ? 'text.primary' : 'text.tertiary'} variant="MainBodyRegular">
          {value ? format(value, "MMM d, yyyy 'at' h:mm a") : placeholder}
        </Text>
        <CalendarMonth color={disabled ? 'text.disabled' : 'text.secondary'} />
      </chakra.button>
    </Stack>
  );
}

// ── ZoneBookingCard ───────────────────────────────────────────────────────────

export interface ZoneBookingEventDetails {
  /** Event title shown under "Event details". */
  title: string;
  /** Optional venue / secondary line. */
  venue?: string;
}

export interface ZoneBookingCardProps {
  /**
   * When set, hides the date picker and shows a locked Event details block
   * instead — used for event-rate / event-search bookings where the window is
   * already padded from the ticket.
   */
  eventDetails?: ZoneBookingEventDetails;
  /** URL for the "Find parking nearby" fallback button shown when the lot is unavailable. */
  findParkingUrl?: string;
  /** Pre-fills the end date. */
  initialEndDate?: Date;
  /** Pre-fills the start date. */
  initialStartDate?: Date;
  /** Called when the user confirms Book Now with a valid window. */
  onBookNow: (start: Date, end: Date) => void;
  /**
   * Optional availability check. When omitted the card skips the
   * available/unavailable/loading states and Book Now is always enabled once
   * both dates are set.
   */
  onCheckAvailability?: (
    start: Date,
    end: Date,
    signal: AbortSignal,
  ) => Promise<{ available: boolean; price?: number }>;
  /**
   * Called whenever both start and end are set (including after the user edits
   * them). Use to keep a parent search window / URL in sync with the card.
   */
  onDatesChange?: (start: Date, end: Date) => void;
}

type AvailStatus =
  | { status: 'idle' }
  | { status: 'loading' }
  | { price: number; status: 'available' }
  | { status: 'unavailable' }
  | { status: 'error' };

export function ZoneBookingCard({
  eventDetails,
  findParkingUrl,
  initialEndDate,
  initialStartDate,
  onBookNow,
  onCheckAvailability,
  onDatesChange,
}: ZoneBookingCardProps) {
  const [startDate, setStartDate] = useState<Date | undefined>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate);
  const [activePicker, setActivePicker] = useState<'start' | 'mobile' | null>(null);
  const [mobileSubPicker, setMobileSubPicker] = useState<'start' | 'end' | null>(null);
  const [pendingMobileDate, setPendingMobileDate] = useState<Date | undefined>();
  // The pending date is seeded on open to pre-fill the Time dropdown, so it cannot
  // double as the calendar's selection — that would paint the seeded day (usually
  // today) as chosen before the user tapped it.
  const [pickedMobileDay, setPickedMobileDay] = useState<Date | undefined>();
  // Bumped on each open so the calendar remounts and focuses its seeded month.
  const [mobileSubPickerSession, setMobileSubPickerSession] = useState(0);
  const [availability, setAvailability] = useState<AvailStatus>({ status: 'idle' });
  const inlineCalendarRef = useRef<HTMLDivElement>(null);

  // Keep local dates aligned when the parent search window changes (e.g. header picker).
  useEffect(() => {
    setStartDate((prev) =>
      prev?.getTime() === initialStartDate?.getTime() ? prev : initialStartDate,
    );
    setEndDate((prev) => (prev?.getTime() === initialEndDate?.getTime() ? prev : initialEndDate));
  }, [initialStartDate, initialEndDate]);

  const startTimeOptions = useMemo(
    () => getSelectableTimeOptions(startDate ?? getEarliestSelectableDateTime()),
    [startDate],
  );
  const endTimeOptions = useMemo(
    () => getSelectableTimeOptions(endDate, startDate),
    [endDate, startDate],
  );

  useEffect(() => {
    // Locked event windows can land off the 15-min selectable grid (e.g. :01
    // after a same-day cushion clamp). Do not snap — Book Now must keep the
    // cushioned times from the parent.
    if (eventDetails || !startDate || startTimeOptions.length === 0) return;
    if (!startTimeOptions.some(({ value }) => value === format(startDate, 'HH:mm'))) {
      setStartDate(dateValueToDate(dateToDateValue(startDate), startTimeOptions[0].value));
    }
  }, [eventDetails, startDate, startTimeOptions]);

  useEffect(() => {
    if (eventDetails || !endDate || endTimeOptions.length === 0) return;
    if (!endTimeOptions.some(({ value }) => value === format(endDate, 'HH:mm'))) {
      setEndDate(dateValueToDate(dateToDateValue(endDate), endTimeOptions[0].value));
    }
  }, [endDate, endTimeOptions, eventDetails]);

  // After the inline calendar mounts, scroll the field+menu into the location
  // details panel — not the window — so Starting/Ending times aren't clipped.
  useLayoutEffect(() => {
    if (activePicker !== 'start') return;
    const el = inlineCalendarRef.current;
    if (!el) return;
    scrollChildIntoNearestOverflow(el);
  }, [activePicker]);

  useEffect(() => {
    if (activePicker !== 'mobile') return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [activePicker]);

  // Propagate user edits to the parent. Skip when local dates already match the
  // parent window — otherwise mount/sync would re-commit and can loop with
  // header/URL updates that feed initialStart/End back into this card.
  useEffect(() => {
    if (!startDate || !endDate || !onDatesChange) return;
    if (
      startDate.getTime() === initialStartDate?.getTime() &&
      endDate.getTime() === initialEndDate?.getTime()
    ) {
      return;
    }
    onDatesChange(startDate, endDate);
  }, [startDate, endDate, onDatesChange, initialStartDate, initialEndDate]);

  useEffect(() => {
    if (!startDate || !endDate || !onCheckAvailability) {
      startTransition(() => setAvailability({ status: 'idle' }));
      return undefined;
    }

    const controller = new AbortController();
    startTransition(() => setAvailability({ status: 'loading' }));

    const check = async () => {
      try {
        const data = await onCheckAvailability(startDate, endDate, controller.signal);
        if (data.available && data.price !== undefined) {
          setAvailability({ price: data.price, status: 'available' });
        } else {
          setAvailability({ status: 'unavailable' });
        }
      } catch (e: unknown) {
        if (!(e instanceof DOMException && e.name === 'AbortError')) {
          setAvailability({ status: 'error' });
        }
      }
    };

    check();
    return () => controller.abort();
  }, [startDate, endDate, onCheckAvailability]);

  const openMobile = () => {
    setMobileSubPicker(null);
    setActivePicker('mobile');
  };

  const openMobileSubPicker = (field: 'start' | 'end') => {
    const existing = field === 'start' ? startDate : endDate;
    // Seed a usable default when nothing is picked yet so Submit isn't dead on open.
    const seed =
      existing ??
      (field === 'end' && startDate
        ? addHours(startDate, 2)
        : getEarliestSelectableDateTime(field === 'end' ? startDate : undefined));
    setPendingMobileDate(seed);
    setPickedMobileDay(undefined);
    setMobileSubPickerSession((session) => session + 1);
    setMobileSubPicker(field);
  };

  const commitMobileSubPicker = () => {
    if (!pendingMobileDate) return;
    if (mobileSubPicker === 'start') {
      setStartDate(pendingMobileDate);
      if (!endDate || endDate <= pendingMobileDate) {
        setEndDate(addHours(pendingMobileDate, 2));
      }
    } else if (mobileSubPicker === 'end') {
      setEndDate(pendingMobileDate);
    }
    setMobileSubPicker(null);
  };

  const mobileSubPickerMinDate = mobileSubPicker === 'end' ? startDate : undefined;
  // Highlight only an already-chosen date or a day tapped in this session.
  const mobileSubPickerSelectedDay =
    pickedMobileDay ?? (mobileSubPicker === 'end' ? endDate : startDate);
  const mobileSubPickerTimeOfDay = pendingMobileDate ? format(pendingMobileDate, 'HH:mm') : '12:00';
  const mobileSubPickerTimeOptions = getSelectableTimeOptions(
    pendingMobileDate,
    mobileSubPickerMinDate,
  );

  const handleBookNow = () => {
    if (!startDate || !endDate) return;
    onBookNow(startDate, endDate);
  };

  const bothDatesSet = !!(startDate && endDate);
  const datesLocked = !!eventDetails && bothDatesSet;
  // When no availability check is wired, Book Now is enabled whenever both dates are set.
  const isLoading = availability.status === 'loading';
  const isUnavailable = availability.status === 'unavailable' || availability.status === 'error';
  const isBookNowDisabled = onCheckAvailability
    ? bothDatesSet && availability.status !== 'available'
    : false;

  const errorCard = isUnavailable && (
    <InformationCard
      body={
        availability.status === 'error'
          ? 'Something went wrong checking availability. Please try again.'
          : 'Sorry, there are no reservations available at the time you selected. Modify your time or find a nearby parking lot.'
      }
      variant="alert"
    />
  );

  const totalRow = availability.status === 'available' && (
    <HStack justify="space-between">
      <Text variant="Subheader" fontWeight="600">
        Total
      </Text>
      <Text variant="Subheader" fontWeight="600">
        {UsDollarWithCents.format(availability.price)}
      </Text>
    </HStack>
  );

  const desktopBookNow = (
    <Button
      disabled={isBookNowDisabled}
      loading={isLoading}
      size="md"
      variant="brand"
      w="full"
      onClick={() => {
        if (!startDate) {
          setActivePicker('start');
          return;
        }
        if (!endDate) {
          setActivePicker('start');
          return;
        }
        handleBookNow();
      }}
    >
      <CalendarMonth />
      Book now
    </Button>
  );

  const mobileBookNow = (
    <Button
      disabled={isBookNowDisabled}
      loading={isLoading}
      size="md"
      variant="brand"
      w="full"
      onClick={() => {
        if (!startDate || !endDate) {
          openMobile();
          return;
        }
        handleBookNow();
      }}
    >
      <CalendarMonth />
      Book now
    </Button>
  );

  const findNearbyButton = availability.status === 'unavailable' && findParkingUrl && (
    <Button asChild size="md" variant="secondary" w="full">
      <a href={findParkingUrl} rel="noopener noreferrer" target="_blank">
        Find parking nearby
      </a>
    </Button>
  );

  const helperText = availability.status === 'idle' && !datesLocked && (
    <Text color="text.secondary" textAlign="center" variant="SmallBodyMedium">
      Select date &amp; time to see rates and book.
    </Text>
  );

  let mobileWhenSection: ReactNode;
  if (datesLocked && eventDetails) {
    mobileWhenSection = (
      <Stack gap="1">
        <Text variant="SmallBodyMedium" color="text.secondary">
          Event details
        </Text>
        <Text variant="MainBodyMedium" color="text.primary">
          {eventDetails.title}
        </Text>
        {eventDetails.venue && (
          <Text variant="SmallBodyRegular" color="text.secondary">
            {eventDetails.venue}
          </Text>
        )}
        {startDate && endDate && (
          <Text variant="SmallBodyRegular" color="text.secondary">
            {formatDateRange(startDate, endDate)}
          </Text>
        )}
      </Stack>
    );
  } else if (startDate && endDate) {
    mobileWhenSection = (
      <HStack align="start" justify="space-between">
        <Stack align="start" gap="2">
          <Text variant="MainBodyMedium">{formatDateRange(startDate, endDate)}</Text>
          <Tag size="sm" startElement={<Schedule />}>
            {formatDuration(startDate, endDate)}
          </Tag>
        </Stack>
        <IconButton aria-label="Edit dates" size="sm" variant="outline" onClick={openMobile}>
          <Edit />
        </IconButton>
      </HStack>
    );
  } else {
    mobileWhenSection = (
      <DateInputBox
        isOpen={false}
        label="When?"
        placeholder="Select your time"
        onOpen={openMobile}
      />
    );
  }

  return (
    <>
      {/* ── Desktop — single "When?" bar (matches Figma) ─────────────────── */}
      <Stack
        bg="fill.background.accent"
        borderRadius="2xl"
        gap="2"
        hideBelow="md"
        pb="6"
        pt="4"
        px="4"
      >
        <SectionHeader title="Reserve parking">
          {datesLocked && eventDetails ? undefined : 'Secure your spot in seconds.'}
        </SectionHeader>

        <Stack gap="4" pt="4">
          {errorCard}

          {datesLocked && eventDetails ? (
            <Stack gap="1">
              <Text variant="SmallBodyMedium" color="text.secondary">
                Event details
              </Text>
              <Text variant="MainBodyMedium" color="text.primary">
                {eventDetails.title}
              </Text>
              {eventDetails.venue && (
                <Text variant="SmallBodyRegular" color="text.secondary">
                  {eventDetails.venue}
                </Text>
              )}
              {startDate && endDate && (
                <Text variant="SmallBodyRegular" color="text.secondary">
                  {formatDateRange(startDate, endDate)}
                </Text>
              )}
            </Stack>
          ) : (
            <Box ref={inlineCalendarRef}>
              <DatePicker
                label="When?"
                type="datetime"
                selectionMode="range"
                useSheet={false}
                inline
                open={activePicker === 'start'}
                onOpenChange={(open) => setActivePicker(open ? 'start' : null)}
                min={dateToDateValue(new Date())}
                value={dateValuesForDatetimeRange(startDate, endDate)}
                startTime={
                  startDate
                    ? format(startDate, 'HH:mm')
                    : format(getEarliestSelectableDateTime(), 'HH:mm')
                }
                endTime={
                  endDate
                    ? format(endDate, 'HH:mm')
                    : format(addHours(startDate ?? getEarliestSelectableDateTime(), 2), 'HH:mm')
                }
                startTimeOptions={startTimeOptions}
                endTimeOptions={endTimeOptions}
                onValueChange={({ value }) => {
                  const [start, end] = value;
                  if (!start) {
                    setStartDate(undefined);
                    setEndDate(undefined);
                    return;
                  }
                  const preferredStartTime = startDate
                    ? format(startDate, 'HH:mm')
                    : format(getEarliestSelectableDateTime(), 'HH:mm');
                  const { start: rawStart, end: rawEnd } = datetimesFromPickerDays(
                    start,
                    end,
                    preferredStartTime,
                    previousEndTimeIfStartUnchanged(
                      start,
                      startDate,
                      endDate ? format(endDate, 'HH:mm') : undefined,
                    ),
                  );
                  const nextStart = combineDateWithSelectableTime(rawStart, preferredStartTime);
                  if (!nextStart) return;
                  setStartDate(nextStart);
                  setEndDate(
                    rawEnd > nextStart ? rawEnd : addDefaultReservationDuration(nextStart),
                  );
                }}
                onStartTimeChange={(time) => {
                  const day = startDate ?? getEarliestSelectableDateTime();
                  const nextStart = combineDateWithSelectableTime(day, time);
                  if (!nextStart) return;
                  setStartDate(nextStart);
                  setEndDate(addHours(nextStart, 2));
                }}
                onEndTimeChange={(time) => {
                  setEndDate((prev) =>
                    prev ? dateValueToDate(dateToDateValue(prev), time) : prev,
                  );
                }}
              />
            </Box>
          )}
        </Stack>

        {datesLocked && eventDetails && <Separator borderColor="stroke.divider.default" />}

        <Stack gap="2" mt="4">
          {totalRow}
          {!(datesLocked && eventDetails) && totalRow && (
            <Separator borderColor="stroke.divider.default" />
          )}
          {desktopBookNow}
          {findNearbyButton}
          {helperText}
        </Stack>
      </Stack>

      {/* ── Mobile card ────────────────────────────────────────────────────
          Leading divider is owned by LocationDetailsSheet (full-bleed SectionDivider). */}
      <Stack
        gap="6"
        hideFrom="md"
        bg="fill.background.accent"
        borderRadius="2xl"
        mb="2"
        pb="6"
        pt="4"
        px="4"
      >
        <Stack gap="6">
          <SectionHeader title="Reserve parking">Secure your spot in seconds.</SectionHeader>

          {errorCard}

          {mobileWhenSection}
        </Stack>

        <Stack gap="4">
          {totalRow}
          {mobileBookNow}
          {findNearbyButton}
          {helperText}
        </Stack>
      </Stack>

      {/* ── Mobile overlay level 1: two inputs + Submit ───────────────────── */}
      {activePicker === 'mobile' && mobileSubPicker === null && (
        <Box
          bg="fill.background.default"
          display="flex"
          flexDirection="column"
          hideFrom="md"
          inset="0"
          position="fixed"
          zIndex={1001}
        >
          <SubpageHeader
            title="Reserve your parking spot"
            onBackClick={() => setActivePicker(null)}
          />

          <Box flex="1" overflowY="auto" px="4" pt="4">
            <Stack gap="4">
              <DateInputBox
                label="Starts"
                value={startDate}
                onOpen={() => openMobileSubPicker('start')}
              />
              <DateInputBox
                label="Ends"
                value={endDate}
                onOpen={() => openMobileSubPicker('end')}
              />
            </Stack>
          </Box>

          <Box bg="fill.background.default" flexShrink={0} px="4" pt="2" pb="6">
            <Button
              disabled={!startDate || !endDate}
              size="lg"
              variant="primary"
              w="full"
              onClick={() => setActivePicker(null)}
            >
              Submit
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Mobile overlay level 2: individual date+time picker ───────────── */}
      {activePicker === 'mobile' && mobileSubPicker !== null && (
        <Box
          bg="fill.background.default"
          display="flex"
          flexDirection="column"
          hideFrom="md"
          inset="0"
          position="fixed"
          zIndex={1002}
        >
          <HStack
            align="center"
            bg="fill.background.default"
            flexShrink={0}
            justify="space-between"
            w="full"
          >
            <Box w="56px" h="56px" flexShrink={0} />
            <Text variant="MainBodyDemiBold" color="text.primary" flex={1} textAlign="center">
              {mobileSubPicker === 'start' ? 'Starts' : 'Ends'}
            </Text>
            <IconButton
              aria-label="Close"
              variant="ghost"
              w="56px"
              h="56px"
              flexShrink={0}
              onClick={() => setMobileSubPicker(null)}
            >
              <Close />
            </IconButton>
          </HStack>

          <Box flex="1" overflowY="auto" p="4">
            <ChakraDatePicker.Root
              key={mobileSubPickerSession}
              inline
              hideOutsideDays
              size="md"
              startOfWeek={0}
              selectionMode="single"
              value={
                mobileSubPickerSelectedDay ? [dateToDateValue(mobileSubPickerSelectedDay)] : []
              }
              // Open on the seeded month even when nothing is selected.
              defaultFocusedValue={
                pendingMobileDate ? dateToDateValue(pendingMobileDate) : undefined
              }
              min={
                mobileSubPickerMinDate
                  ? dateToDateValue(mobileSubPickerMinDate)
                  : dateToDateValue(new Date())
              }
              onValueChange={({ value: nextValue }) => {
                const day = nextValue[0];
                if (!day) return;
                const combined = combineDateWithSelectableTime(
                  dateValueToDate(day, mobileSubPickerTimeOfDay),
                  mobileSubPickerTimeOfDay,
                  mobileSubPickerMinDate,
                );
                if (!combined) return;
                setPendingMobileDate(combined);
                setPickedMobileDay(combined);
              }}
            >
              <ChakraDatePicker.Content unstyled>
                <DatePickerMenuBody type="datetime" selectionMode="single" />
                <DateTimeSection
                  value={pendingMobileDate ? [dateToDateValue(pendingMobileDate)] : []}
                  startTime={mobileSubPickerTimeOfDay}
                  startTimeOptions={mobileSubPickerTimeOptions}
                  selectionMode="single"
                  px="0"
                  onStartTimeChange={(time) => {
                    if (!pendingMobileDate) return;
                    setPendingMobileDate(dateValueToDate(dateToDateValue(pendingMobileDate), time));
                  }}
                />
              </ChakraDatePicker.Content>
            </ChakraDatePicker.Root>
          </Box>

          <Box bg="fill.background.default" flexShrink={0} px="4" pt="2" pb="6">
            <Button
              disabled={!pendingMobileDate}
              size="lg"
              variant="primary"
              w="full"
              onClick={commitMobileSubPicker}
            >
              Submit
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}
